import React, { useState } from "react";
import { motion, AnimatePresence } from "framer-motion";
import { toast } from "sonner";
import api from "@/lib/api";
import {
  BrainIcon, CircleNotchIcon, LightbulbIcon, RocketLaunchIcon, QuotesIcon, TargetIcon, ImageIcon,
} from "@phosphor-icons/react";

const impactColor = (imp = "") => (imp.toLowerCase().includes("high") ? "#16C784" : "#A3E635");

const durSeconds = (iso) => {
  const m = /PT(?:(\d+)H)?(?:(\d+)M)?(?:(\d+)S)?/.exec(iso || "");
  if (!m) return 0;
  return (+(m[1] || 0)) * 3600 + (+(m[2] || 0)) * 60 + (+(m[3] || 0));
};

// A channel is treated as Shorts-first when most of its videos are <= 60s.
const isShortsChannel = (videos) => {
  const withDur = (videos || []).filter((v) => v.duration && durSeconds(v.duration) > 0);
  if (withDur.length < 3) return false;
  const shortCount = withDur.filter((v) => durSeconds(v.duration) <= 60).length;
  return shortCount / withDur.length >= 0.5;
};

const buildImagePrompt = (s, isShorts) => {
  const parts = isShorts
    ? [
        `Create a scroll-stopping, high-CTR 9:16 vertical YouTube Shorts cover for a video titled "${s.title}".`,
        s.concept ? `Video concept: ${s.concept}` : "",
        s.content_type ? `Content style: ${s.content_type}.` : "",
        `Compose it for a 9:16 full-screen mobile display: single clear focal subject centered and kept safely away from the top and bottom edges (avoid the Shorts UI zones), dramatic cinematic lighting, vivid saturated colors, exaggerated scale and drama, and a strong emotional/"wow" reaction.`,
        `Leave clean vertical negative space for large punchy title text. Ultra-detailed, professional, photorealistic, crisp, no watermarks, no gibberish text.`,
      ]
    : [
        `Create a scroll-stopping, high-CTR 16:9 YouTube thumbnail for a video titled "${s.title}".`,
        s.concept ? `Video concept: ${s.concept}` : "",
        s.content_type ? `Content style: ${s.content_type}.` : "",
        `Make it bold and high-contrast with a single clear focal subject, dramatic cinematic lighting, vivid saturated colors, exaggerated scale and drama, and a strong emotional/"wow" reaction.`,
        `Leave clean negative space on one side for large punchy title text. Ultra-detailed, professional, photorealistic, crisp, no watermarks, no gibberish text.`,
      ];
  return parts.filter(Boolean).join("\n");
};

const AISuggestions = ({ initial, channelId, videos }) => {
  const [data, setData] = useState(initial || null);
  const [loading, setLoading] = useState(false);
  const shorts = isShortsChannel(videos);

  const generate = async () => {
    setLoading(true);
    try {
      const res = await api.post(`/ai/suggestions${channelId ? `?channel_id=${channelId}` : ""}`);
      setData(res.data);
      toast.success("Content Overwatch mapped your next 3 moves");
    } catch (e) {
      toast.error(e.response?.data?.detail || "Suggestion engine failed");
    } finally {
      setLoading(false);
    }
  };

  const copyPrompt = async (s) => {
    const text = buildImagePrompt(s, shorts);
    try {
      await navigator.clipboard.writeText(text);
      toast.success(`Image prompt copied (${shorts ? "9:16 vertical" : "16:9"}) — paste it into your AI image tool`);
    } catch (e) {
      const ta = document.createElement("textarea");
      ta.value = text;
      document.body.appendChild(ta);
      ta.select();
      try { document.execCommand("copy"); toast.success("Image prompt copied"); }
      catch (_) { toast.error("Could not copy — select and copy manually"); }
      document.body.removeChild(ta);
    }
  };

  return (
    <motion.div
      data-testid="ai-suggestions"
      initial={{ opacity: 0, y: 15 }}
      animate={{ opacity: 1, y: 0 }}
      transition={{ delay: 0.25 }}
      className="relative bg-[#0C0C0E] border border-[#A3E635]/30 p-5 md:p-6"
      style={{ boxShadow: "0 0 15px rgba(163, 230, 53,0.08), inset 0 1px 0 rgba(255,255,255,0.05)" }}
    >
      <img
        src="https://images.unsplash.com/photo-1591981864041-afda2ddf5031?crop=entropy&cs=srgb&fm=jpg&ixid=M3w4NjA1MDV8MHwxfHNlYXJjaHw0fHxmdXR1cmlzdGljJTIwZGFyayUyMGdyZWVuJTIwdGVjaG5vbG9neSUyMGFic3RyYWN0fGVufDB8fHxncmVlbnwxNzg2NzQ5NDc0fDA&ixlib=rb-4.1.0&q=85"
        alt=""
        className="absolute top-0 right-0 w-40 h-full object-cover opacity-[0.07] pointer-events-none"
      />
      <div className="relative flex items-start justify-between gap-3 mb-4">
        <div className="flex items-center gap-3">
          <div className="w-10 h-10 rounded-full border border-[#A3E635]/50 flex items-center justify-center">
            <BrainIcon size={20} className="text-[#A3E635]" weight="fill" />
          </div>
          <div>
            <div className="text-[11px] font-bold uppercase tracking-[0.2em] text-[#A3E635]">// Content Overwatch Strategy</div>
            <h2 className="font-rajdhani font-bold text-xl tracking-wide">NEXT 3 MISSIONS</h2>
          </div>
        </div>
        <button
          data-testid="generate-suggestions-button"
          onClick={generate}
          disabled={loading}
          className="flex items-center gap-2 bg-[#A3E635] hover:bg-[#84CC16] text-black disabled:opacity-50 font-rajdhani font-bold uppercase tracking-wider text-xs px-4 py-2 transition-colors duration-200 shrink-0"
        >
          {loading ? <CircleNotchIcon size={14} className="spin-slow" /> : <RocketLaunchIcon size={14} weight="fill" />}
          {data ? "Regenerate" : "Generate"}
        </button>
      </div>

      {data?.summary && (
        <div className="relative flex gap-2 border-l-2 border-[#A3E635] pl-3 mb-5">
          <p className="text-sm text-[#d4d4d8] italic">{data.summary}</p>
        </div>
      )}

      <AnimatePresence mode="wait">
        {!data ? (
          <div className="relative py-10 text-center">
            <LightbulbIcon size={32} className="text-[#5a5a63] mx-auto mb-3" />
            <p className="font-mono text-xs text-[#5a5a63] max-w-xs mx-auto">
              Deploy Content Overwatch to study your top performers and command the next 3 videos to make.
            </p>
          </div>
        ) : (
          <div className="relative space-y-3">
            {(data.suggestions || []).map((s, i) => (
              <motion.div
                key={i}
                data-testid={`suggestion-${i}`}
                initial={{ opacity: 0, x: 15 }}
                animate={{ opacity: 1, x: 0 }}
                transition={{ delay: i * 0.12 }}
                className="border border-[#222226] bg-[#050505] p-4 hover:border-[#A3E635]/40 transition-colors duration-200"
              >
                <div className="flex items-start justify-between gap-3">
                  <div className="flex items-start gap-3">
                    <span className="font-rajdhani font-bold text-2xl text-[#A3E635]/40 leading-none">
                      {String(i + 1).padStart(2, "0")}
                    </span>
                    <h3 className="font-rajdhani font-bold text-base text-white leading-snug">{s.title}</h3>
                  </div>
                  <span
                    className="font-mono text-[10px] uppercase tracking-wider px-2 py-0.5 border shrink-0"
                    style={{ color: impactColor(s.predicted_impact), borderColor: impactColor(s.predicted_impact) + "66" }}
                  >
                    {s.predicted_impact}
                  </span>
                </div>
                <p className="text-sm text-[#A0A0AB] mt-2">{s.concept}</p>
                {s.content_type && (
                  <div className="mt-2 inline-flex items-center gap-1 font-mono text-[10px] uppercase tracking-wider text-[#A3E635] border border-[#A3E635]/40 px-2 py-0.5">
                    <TargetIcon size={11} weight="fill" /> {s.content_type}
                  </div>
                )}
                <p className="text-xs text-[#d4d4d8] mt-2"><span className="text-[#A3E635]">WHY:</span> {s.why}</p>
                {s.hook && (
                  <div className="flex items-start gap-1.5 mt-3 pt-3 border-t border-[#1a1a1e]">
                    <QuotesIcon size={14} className="text-[#16C784] shrink-0 mt-0.5" weight="fill" />
                    <span className="text-xs text-[#d4d4d8] italic">{s.hook}</span>
                  </div>
                )}
                <button
                  data-testid={`copy-prompt-${i}`}
                  onClick={() => copyPrompt(s)}
                  className="mt-3 w-full flex items-center justify-center gap-2 border border-[#16C784]/40 bg-[#16C784]/5 hover:bg-[#16C784]/15 text-[#16C784] font-rajdhani font-bold uppercase tracking-wider text-[11px] px-3 py-2 transition-colors duration-200"
                >
                  <ImageIcon size={14} weight="fill" /> Copy image prompt · {shorts ? "9:16" : "16:9"}
                </button>
              </motion.div>
            ))}
          </div>
        )}
      </AnimatePresence>
    </motion.div>
  );
};

export default AISuggestions;
